import { useState } from 'react';
import { X, Check, Copy, Mail, User, Shield, Send, CheckCircle2, UserPlus } from 'lucide-react';

const InviteUserModal = ({ isOpen, onClose, onInvite, roles, t }) => {
    const [user, setUser] = useState({ email: '', role: '', firstName: '', lastName: '' });
    const [link, setLink] = useState(null);
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const inviteLink = await onInvite(user);
            setLink(inviteLink);
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(link);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleClose = () => {
        setUser({ email: '', role: '', firstName: '', lastName: '' });
        setLink(null);
        setLoading(false);
        setCopied(false);
        onClose();
    };

    const isFormValid = user.firstName && user.lastName && user.email && user.role;

    return (
        <div
            onClick={handleClose}
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '1rem',
                animation: 'inviteOverlayIn 0.2s ease',
            }}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    maxWidth: '460px',
                    width: '100%',
                    background: 'var(--bg-card)',
                    borderRadius: '24px',
                    overflow: 'hidden',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                    animation: 'inviteModalIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
            >
                {/* Header */}
                <div style={{
                    padding: '1.5rem 1.5rem 1.25rem',
                    borderBottom: '1px solid var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '12px',
                            background: 'var(--primary-light)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--primary)',
                        }}>
                            <UserPlus size={20} />
                        </div>
                        <div>
                            <h2 style={{ fontSize: '1.1rem', fontWeight: 800, margin: 0 }}>{t('roles.inviteUser')}</h2>
                            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0, fontWeight: 500 }}>
                                Ekibinize yeni bir üye ekleyin
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        style={{
                            background: 'var(--bg-main)',
                            border: 'none',
                            borderRadius: '10px',
                            width: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: 'var(--text-muted)',
                        }}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div style={{ padding: '1.5rem' }}>
                    {link ? (
                        <div style={{ textAlign: 'center' }}>
                            <div style={{
                                width: '64px',
                                height: '64px',
                                borderRadius: '20px',
                                background: '#dcfce7',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 1.25rem',
                            }}>
                                <CheckCircle2 size={32} style={{ color: '#22c55e' }} />
                            </div>
                            <h3 style={{ fontSize: '1.1rem', fontWeight: 800, margin: '0 0 0.5rem 0' }}>
                                Davet Oluşturuldu!
                            </h3>
                            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: '0 0 1.5rem 0' }}>
                                Aşağıdaki linki <strong>{user.firstName}</strong>'e gönderin
                            </p>
                            <div style={{
                                background: 'var(--bg-main)',
                                padding: '12px 14px',
                                borderRadius: '14px',
                                border: '1px solid var(--border)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                marginBottom: '1.5rem',
                            }}>
                                <input
                                    readOnly
                                    value={link}
                                    style={{
                                        flex: 1,
                                        fontSize: '0.75rem',
                                        border: 'none',
                                        background: 'transparent',
                                        color: 'var(--text-main)',
                                        fontWeight: 500,
                                        outline: 'none',
                                        minWidth: 0,
                                    }}
                                />
                                <button
                                    onClick={handleCopy}
                                    style={{
                                        background: copied ? '#22c55e' : 'var(--primary)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '10px',
                                        padding: '8px 14px',
                                        fontSize: '0.75rem',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        flexShrink: 0,
                                        transition: 'background 0.2s',
                                    }}
                                >
                                    {copied ? <Check size={14} /> : <Copy size={14} />}
                                    {copied ? 'Kopyalandı' : 'Kopyala'}
                                </button>
                            </div>
                            <button
                                onClick={handleClose}
                                style={{
                                    width: '100%',
                                    padding: '14px',
                                    borderRadius: '14px',
                                    border: '1px solid var(--border)',
                                    background: 'var(--bg-main)',
                                    color: 'var(--text-main)',
                                    fontSize: '0.9rem',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                }}
                            >
                                Kapat
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            {/* Ad Soyad */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, marginBottom: '6px', color: 'var(--text-muted)' }}>
                                        Ad
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <User size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', opacity: 0.5 }} />
                                        <input
                                            required
                                            placeholder="Adı"
                                            value={user.firstName}
                                            onChange={e => setUser({ ...user, firstName: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '12px 12px 12px 38px',
                                                borderRadius: '12px',
                                                border: '1px solid var(--border)',
                                                background: 'var(--bg-main)',
                                                fontSize: '0.875rem',
                                                fontWeight: 500,
                                                color: 'var(--text-main)',
                                            }}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, marginBottom: '6px', color: 'var(--text-muted)' }}>
                                        Soyad
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <User size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', opacity: 0.5 }} />
                                        <input
                                            required
                                            placeholder="Soyadı"
                                            value={user.lastName}
                                            onChange={e => setUser({ ...user, lastName: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '12px 12px 12px 38px',
                                                borderRadius: '12px',
                                                border: '1px solid var(--border)',
                                                background: 'var(--bg-main)',
                                                fontSize: '0.875rem',
                                                fontWeight: 500,
                                                color: 'var(--text-main)',
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* E-posta */}
                            <div style={{ marginBottom: '12px' }}>
                                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, marginBottom: '6px', color: 'var(--text-muted)' }}>
                                    E-posta
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <Mail size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', opacity: 0.5 }} />
                                    <input
                                        type="email"
                                        required
                                        placeholder="ornek@email.com"
                                        value={user.email}
                                        onChange={e => setUser({ ...user, email: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '12px 12px 12px 38px',
                                            borderRadius: '12px',
                                            border: '1px solid var(--border)',
                                            background: 'var(--bg-main)',
                                            fontSize: '0.875rem',
                                            fontWeight: 500,
                                            color: 'var(--text-main)',
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Rol */}
                            <div style={{ marginBottom: '1.75rem' }}>
                                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, marginBottom: '6px', color: 'var(--text-muted)' }}>
                                    Rol
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <Shield size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', opacity: 0.5, pointerEvents: 'none' }} />
                                    <select
                                        required
                                        value={user.role}
                                        onChange={e => setUser({ ...user, role: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '12px 12px 12px 38px',
                                            borderRadius: '12px',
                                            border: '1px solid var(--border)',
                                            background: 'var(--bg-main)',
                                            fontSize: '0.875rem',
                                            fontWeight: 500,
                                            color: user.role ? 'var(--text-main)' : 'var(--text-muted)',
                                            appearance: 'none',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <option value="">Rol seçin...</option>
                                        {roles.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                                    </select>
                                    <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                                    </div>
                                </div>
                            </div>

                            {/* Butonlar */}
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    style={{
                                        flex: 1,
                                        padding: '14px',
                                        borderRadius: '14px',
                                        border: '1px solid var(--border)',
                                        background: 'transparent',
                                        color: 'var(--text-main)',
                                        fontSize: '0.9rem',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                    }}
                                >
                                    İptal
                                </button>
                                <button
                                    type="submit"
                                    disabled={!isFormValid || loading}
                                    className="btn-primary"
                                    style={{
                                        flex: 1,
                                        padding: '14px',
                                        borderRadius: '14px',
                                        border: 'none',
                                        fontSize: '0.9rem',
                                        fontWeight: 700,
                                        cursor: isFormValid && !loading ? 'pointer' : 'not-allowed',
                                        opacity: isFormValid && !loading ? 1 : 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                    }}
                                >
                                    <Send size={16} />
                                    {loading ? 'Gönderiliyor...' : 'Davet Gönder'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>

            <style>{`
                @keyframes inviteOverlayIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes inviteModalIn {
                    from { transform: translateY(20px) scale(0.97); opacity: 0; }
                    to { transform: translateY(0) scale(1); opacity: 1; }
                }
            `}</style>
        </div>
    );
};

export default InviteUserModal;
